import { useState, useEffect } from 'react';
import { getAllArticles, importArticles, deleteArticle, toggleArticleStatus, generateImportTemplate, updateArticleAudio, getArticleDetail, updateParagraphTimestamps, saveArticleDetail } from '../utils/articleManager';
import { Link } from 'react-router-dom';
import AdminProtected from '../components/AdminProtected';

export default function ArticleManager() {
  const [articles, setArticles] = useState([]);
  const [importJson, setImportJson] = useState('');
  const [importResult, setImportResult] = useState(null);
  const [showTemplate, setShowTemplate] = useState(false);
  const [copied, setCopied] = useState(false);
  // 从配置文件加载音频列表（只显示实际存在的音频文件）
  const getDefaultAudioFiles = () => {
    const stored = localStorage.getItem('audioFiles');
    if (stored) {
      return JSON.parse(stored);
    }
    // 默认为空数组，等待从配置文件加载
    return [];
  };
  
  const [audioFiles, setAudioFiles] = useState(getDefaultAudioFiles);
  
  // 保存音频文件列表到localStorage
  const saveAudioFiles = (files) => {
    setAudioFiles(files);
    localStorage.setItem('audioFiles', JSON.stringify(files));
  };
  
  // 编辑模态框状态
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingArticle, setEditingArticle] = useState(null);
  const [articleDetail, setArticleDetail] = useState(null);
  const [manualAudioName, setManualAudioName] = useState('');
  const [audioDuration, setAudioDuration] = useState(0);
  const [activeEditTab, setActiveEditTab] = useState('timestamps');

  useEffect(() => {
    refreshArticles();
    // 尝试从配置文件加载音频列表
    loadAudioListFromConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 从配置文件加载音频列表
  const loadAudioListFromConfig = async () => {
    try {
      const response = await fetch('/audio/audio_list.json');
      if (response.ok) {
        const configFiles = await response.json();
        if (Array.isArray(configFiles)) {
          // 直接使用配置文件中的音频列表（只显示实际存在的音频文件）
          const audioFileList = configFiles.map(name => ({ name, size: null, isReal: true }));
          saveAudioFiles(audioFileList);
          console.log('从配置文件加载了', audioFileList.length, '个音频文件');
        }
      }
    } catch (error) {
      console.log('未找到音频配置文件');
    }
  };

  const refreshArticles = () => {
    setArticles(getAllArticles());
  };

  const handleImport = () => {
    if (!importJson.trim()) {
      setImportResult({ success: false, message: '请输入JSON数据' });
      return;
    }
    
    const result = importArticles(importJson);
    setImportResult(result);
    if (result.success) {
      setImportJson('');
      refreshArticles();
    }
  };

  // 处理JSON文件上传
  const handleJsonFileUpload = (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    
    let totalImported = 0;
    let processedCount = 0;
    const totalFiles = files.length;
    
    Array.from(files).forEach(file => {
      const reader = new FileReader();
      reader.onload = (event) => {
        try {
          const content = event.target.result;
          const result = importArticles(content);
          if (result.success) {
            totalImported += result.count;
          }
          processedCount++;
          
          if (processedCount === totalFiles) {
            setImportResult({ 
              success: true, 
              message: `成功导入 ${totalImported} 篇文章（共处理 ${totalFiles} 个文件）` 
            });
            refreshArticles();
          }
        } catch (error) {
          processedCount++;
          if (processedCount === totalFiles) {
            setImportResult({ 
              success: totalImported > 0, 
              message: `导入完成：成功 ${totalImported} 篇，失败 ${totalFiles - totalImported} 个文件` 
            });
            refreshArticles();
          }
        }
      };
      reader.readAsText(file);
    });
    
    // 清空文件输入
    e.target.value = '';
  };

  const handleCopyTemplate = () => {
    const template = generateImportTemplate();
    navigator.clipboard.writeText(template).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleAudioUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const maxSize = 50 * 1024 * 1024;
      if (file.size > maxSize) {
        alert('文件大小不能超过50MB');
        return;
      }
      // 标记为已上传的真实文件
      const newAudioFile = { name: file.name, size: file.size, isReal: true };
      saveAudioFiles([...audioFiles, newAudioFile]);
      alert('🎵 音频文件已添加！\n\n文件名: ' + file.name + '\n大小: ' + (file.size / 1024 / 1024).toFixed(2) + 'MB\n\n注意：当前为演示模式，实际文件需要手动上传到 public/audio 文件夹');
    }
  };

  const handleAudioSelect = (articleId, audioFileName) => {
    const success = updateArticleAudio(articleId, audioFileName);
    if (success) {
      refreshArticles();
      alert('✅ 音频关联成功！');
    }
  };

  const handleDeleteAudio = (audioFileName) => {
    const audioFile = audioFiles.find(a => a.name === audioFileName);
    if (audioFile && audioFile.isReal) {
      alert('⚠️ 该音频文件已上传到服务器，无法在此删除。请通过服务器文件管理删除。');
      return;
    }
    
    if (window.confirm(`确定删除 "${audioFileName}" 吗？`)) {
      const articleUsingAudio = articles.find(a => a.audioFile === audioFileName);
      if (articleUsingAudio) {
        alert(`⚠️ 该音频正在被文章 "${articleUsingAudio.title}" 使用，请先解除关联`);
        return;
      }
      saveAudioFiles(audioFiles.filter(a => a.name !== audioFileName));
      alert('🗑️ 音频已删除');
    }
  };

  // 打开编辑模态框
  const openEditModal = async (article) => {
    setEditingArticle(article);
    const detail = getArticleDetail(article.id);
    setArticleDetail(detail || { paragraphs: [] });
    setShowEditModal(true);
    // 加载音频时长
    const audioFile = detail?.audioFile || article.audioFile;
    if (audioFile) {
      loadAudioDuration(audioFile);
    }
  };

  // 关闭编辑模态框
  const closeEditModal = () => {
    setShowEditModal(false);
    setEditingArticle(null);
    setArticleDetail(null);
  };

  // 保存段落时间戳
  const saveParagraphTimestamps = () => {
    if (!editingArticle || !articleDetail) return;
    
    // 确保 audioFile 字段被保留
    const detailToSave = {
      ...articleDetail,
      audioFile: articleDetail.audioFile || editingArticle.audioFile
    };
    
    let savedCount = 0;
    detailToSave.paragraphs.forEach((paragraph, index) => {
      if (paragraph.audioStart !== undefined && paragraph.audioEnd !== undefined) {
        updateParagraphTimestamps(editingArticle.id, index, paragraph.audioStart, paragraph.audioEnd);
        savedCount++;
      }
    });
    
    // 直接保存整个详情对象（包括 audioFile 和 questions）
    saveArticleDetail(editingArticle.id, detailToSave);
    
    // 更新文章列表中的音频文件关联
    if (editingArticle.audioFile !== detailToSave.audioFile && detailToSave.audioFile) {
      updateArticleAudio(editingArticle.id, detailToSave.audioFile);
    }
    
    const questionCount = detailToSave.questions?.length || 0;
    alert(`✅ 成功保存 ${savedCount} 个段落的时间戳和 ${questionCount} 道题目！`);
    refreshArticles();
    closeEditModal();
  };

  // 格式化时长显示
  const formatDuration = (seconds) => {
    if (!seconds || isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.round(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // 加载音频时长
  const loadAudioDuration = async (audioFileName) => {
    if (!audioFileName) {
      setAudioDuration(0);
      return;
    }
    try {
      const audio = new Audio(`/audio/${audioFileName}`);
      audio.addEventListener('loadedmetadata', () => {
        const duration = audio.duration;
        setAudioDuration(duration);
        
        // 自动设置最后一节的结束时间为音频总时长
        if (articleDetail && articleDetail.paragraphs.length > 0) {
          const lastIndex = articleDetail.paragraphs.length - 1;
          const newParagraphs = [...articleDetail.paragraphs];
          if (!newParagraphs[lastIndex].audioEnd || newParagraphs[lastIndex].audioEnd === 0) {
            newParagraphs[lastIndex].audioEnd = Math.round(duration);
            newParagraphs[lastIndex].audioEndSeconds = Math.round(duration);
            setArticleDetail({ ...articleDetail, paragraphs: newParagraphs });
          }
        }
      });
      audio.addEventListener('error', () => {
        setAudioDuration(0);
      });
      audio.load();
    } catch (error) {
      setAudioDuration(0);
    }
  };

  // 将秒转换为分:秒对象
  const secondsToTime = (totalSeconds) => {
    if (!totalSeconds || isNaN(totalSeconds)) {
      return { minutes: 0, seconds: 0 };
    }
    const mins = Math.floor(totalSeconds / 60);
    const secs = totalSeconds % 60;
    return { minutes: mins, seconds: Math.round(secs * 10) / 10 };
  };

  // 更新段落时间戳（带自动填充）
  const updateParaTimestamp = (index, field, value, unit) => {
    if (!articleDetail) return;
    const newParagraphs = [...articleDetail.paragraphs];
    if (!newParagraphs[index]) newParagraphs[index] = {};
    
    // 计算总秒数
    const current = newParagraphs[index];
    if (unit === 'minutes') {
      const seconds = current.audioStartSeconds || 0;
      const secs = seconds % 60;
      current.audioStartSeconds = parseFloat(value) * 60 + secs;
    } else if (unit === 'seconds') {
      const seconds = current.audioStartSeconds || 0;
      const mins = Math.floor(seconds / 60);
      current.audioStartSeconds = mins * 60 + parseFloat(value);
    } else if (unit === 'endMinutes') {
      const seconds = current.audioEndSeconds || 0;
      const secs = seconds % 60;
      current.audioEndSeconds = parseFloat(value) * 60 + secs;
    } else if (unit === 'endSeconds') {
      const seconds = current.audioEndSeconds || 0;
      const mins = Math.floor(seconds / 60);
      current.audioEndSeconds = mins * 60 + parseFloat(value);
    }
    
    // 更新显示字段
    current.audioStart = current.audioStartSeconds;
    current.audioEnd = current.audioEndSeconds;
    
    // 自动填充下一段的开始时间（始终同步更新）
    if (field === 'audioEnd' && index < newParagraphs.length - 1) {
      const nextPara = newParagraphs[index + 1];
      if (nextPara) {
        // 只有当当前段有完整的结束时间时才更新
        if (current.audioEndSeconds && !isNaN(current.audioEndSeconds)) {
          nextPara.audioStart = current.audioEnd;
          nextPara.audioStartSeconds = current.audioEndSeconds;
        }
      }
    }
    
    // 反向关联：输入当前段开始时间时，自动更新上一段的结束时间
    if (field === 'audioStart' && index > 0) {
      const prevPara = newParagraphs[index - 1];
      if (prevPara) {
        // 只有当当前段有完整的开始时间时才更新
        if (current.audioStartSeconds && !isNaN(current.audioStartSeconds)) {
          prevPara.audioEnd = current.audioStart;
          prevPara.audioEndSeconds = current.audioStartSeconds;
        }
      }
    }
    
    setArticleDetail({ ...articleDetail, paragraphs: newParagraphs });
  };

  // 更新文章音频文件
  const updateArticleAudioFile = (audioFileName) => {
    if (!articleDetail) return;
    setArticleDetail({ ...articleDetail, audioFile: audioFileName });
  };

  return (
    <AdminProtected>
      <div className="admin-container">
        <div className="admin-header">
          <h1>📚 文章管理</h1>
          <Link to="/" className="btn btn-outline">← 返回首页</Link>
        </div>

      {/* 导入区域 */}
      <div className="import-section">
        <h2>📥 批量导入文章</h2>
        <p className="section-desc">使用AI生成的JSON格式文章数据，批量导入到系统中</p>
        
        <div className="import-controls">
          <button className="btn btn-accent" onClick={() => setShowTemplate(!showTemplate)}>
            {showTemplate ? '隐藏模板' : '📋 查看导入模板'}
          </button>
          <button className="btn btn-outline" onClick={handleCopyTemplate}>
            {copied ? '✅ 已复制' : '📄 复制模板'}
          </button>
        </div>

        {showTemplate && (
          <div className="template-box">
            <pre>{generateImportTemplate()}</pre>
          </div>
        )}

        {/* 文件上传区域 */}
        <div className="file-upload-area" style={{ marginBottom: '15px' }}>
          <label htmlFor="json-file-upload" className="btn btn-outline" style={{ cursor: 'pointer', marginRight: '10px' }}>
            📁 选择JSON文件
          </label>
          <input
            id="json-file-upload"
            type="file"
            accept=".json"
            multiple
            onChange={handleJsonFileUpload}
            style={{ display: 'none' }}
          />
          <span className="hint-text">支持选择多个JSON文件</span>
        </div>

        <div style={{ textAlign: 'center', color: '#888', margin: '10px 0' }}>— 或者粘贴JSON数据 —</div>

        <textarea
          value={importJson}
          onChange={(e) => setImportJson(e.target.value)}
          placeholder="请粘贴AI生成的JSON文章数据..."
          rows={8}
        />

        <button className="btn btn-primary" onClick={handleImport}>
          🚀 开始导入
        </button>

        {importResult && (
          <div className={`import-result ${importResult.success ? 'success' : 'error'}`}>
            {importResult.success ? '✅' : '❌'} {importResult.message}
          </div>
        )}
      </div>

      {/* 音频文件管理 */}
      <div className="audio-upload-section">
        <h2>🎵 音频文件管理</h2>
        <p className="section-desc">🔊 表示已上传到服务器的实际音频 | 🎧 表示演示数据 | 支持 mp3, m4a, wav 格式</p>
        <p className="section-desc">💡 如果您已将音频文件放入 public/audio 文件夹但未显示，请点击下方按钮手动添加</p>
        
        <div className="audio-upload-area">
          <input
            type="file"
            accept="audio/*"
            onChange={handleAudioUpload}
            className="audio-file-input"
            id="audio-upload-input"
          />
          <label htmlFor="audio-upload-input" className="btn btn-accent audio-upload-btn">
            📤 选择音频文件
          </label>
        </div>
        
        {/* 手动添加已上传的音频文件名 */}
        <div className="manual-audio-input">
          <input
            type="text"
            placeholder="输入已上传到 public/audio 的文件名（如: my_audio.m4a）"
            className="manual-audio-input-field"
            value={manualAudioName}
            onChange={(e) => setManualAudioName(e.target.value)}
          />
          <button 
            className="btn btn-secondary" 
            onClick={() => {
              const fileName = manualAudioName.trim();
              if (!fileName) {
                alert('请输入文件名');
                return;
              }
              if (audioFiles.find(a => a.name === fileName)) {
                alert('❌ 该文件名已存在');
                return;
              }
              saveAudioFiles([...audioFiles, { name: fileName, size: null, isReal: true }]);
              setManualAudioName('');
              alert('✅ 音频文件名已添加！');
            }}
          >
            ➕ 添加文件名
          </button>
        </div>
        
        <div className="audio-list">
          <h3>📁 已上传音频 ({audioFiles.length})</h3>
          <div className="audio-items">
            {audioFiles.map((audio) => (
              <div key={audio.name} className="audio-item">
                <span className="audio-icon">{audio.isReal ? '🔊' : '🎧'}</span>
                <span className="audio-name">{audio.name}</span>
                <span className="audio-badge">{audio.isReal ? '已上传' : '演示'}</span>
                {audio.size && <span className="audio-size">{(audio.size / 1024 / 1024).toFixed(2)}MB</span>}
                {!audio.isReal && (
                  <button className="btn btn-sm btn-danger" onClick={() => handleDeleteAudio(audio.name)}>
                    删除
                  </button>
                )}
              </div>
            ))}
            {audioFiles.length === 0 && (
              <p className="empty-audio">暂无上传的音频文件</p>
            )}
          </div>
        </div>

        <div className="audio-guide">
          <h3>📝 音频命名规范</h3>
          <ul>
            <li>建议命名格式：<code>article_&lbrace;文章ID&rbrace;_full.m4a</code></li>
            <li>示例：<code>article_1_full.m4a</code>（对应ID为1的文章）</li>
            <li>段落音频时间戳在编辑文章时配置</li>
          </ul>
        </div>
      </div>

      {/* 文章列表 */}
      <div className="articles-section">
        <h2>📖 文章列表 ({articles.length}篇)</h2>
        
        {articles.length === 0 ? (
          <div className="empty-state">
            <p>暂无文章，使用上方功能导入文章</p>
          </div>
        ) : (
          <div className="articles-grid">
            {articles.map(article => (
              <div key={article.id} className="article-item">
                <div className="article-header">
                  <span className="article-cover">{article.cover}</span>
                  <div className="article-info">
                    <h3>{article.title} <span className="article-id-badge">ID: {article.id}</span></h3>
                    <p className="article-meta">{article.source} · {article.date}</p>
                  </div>
                  <span className={`status-badge ${article.enabled ? 'active' : 'disabled'}`}>
                    {article.enabled ? '启用' : '禁用'}
                  </span>
                </div>
                
                <div className="article-audio">
                  <label>🔊 关联音频：</label>
                  <select 
                    value={article.audioFile || ''}
                    onChange={(e) => handleAudioSelect(article.id, e.target.value || null)}
                    className="audio-select"
                  >
                    <option value="">未关联</option>
                    {audioFiles.map(audio => (
                      <option key={audio.name} value={audio.name}>
                        {audio.name}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div className="article-actions">
                  <Link to={`/article/${article.id}`} className="btn btn-sm btn-primary">
                    阅读
                  </Link>
                  <button 
                    className="btn btn-sm btn-accent" 
                    onClick={() => openEditModal(article)}
                  >
                    ✏️ 编辑
                  </button>
                  <button 
                    className="btn btn-sm btn-outline" 
                    onClick={() => { toggleArticleStatus(article.id); refreshArticles(); }}
                  >
                    {article.enabled ? '禁用' : '启用'}
                  </button>
                  <button 
                    className="btn btn-sm btn-danger" 
                    onClick={() => { 
                      if (window.confirm(`确定删除文章 "${article.title}" 吗？`)) {
                        deleteArticle(article.id); 
                        refreshArticles(); 
                      }
                    }}
                  >
                    删除
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      </div>

      {/* 编辑文章模态框 */}
      {showEditModal && editingArticle && (
        <div className="modal-overlay" onClick={closeEditModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>✏️ 编辑文章：{editingArticle.title}</h2>
              <button className="modal-close" onClick={closeEditModal}>×</button>
            </div>
            
            <div className="modal-body">
              {/* 音频文件选择 */}
              <div className="edit-section">
                <h3>🎵 关联音频文件</h3>
                <select 
                  value={articleDetail?.audioFile || editingArticle.audioFile || ''}
                  onChange={(e) => updateArticleAudioFile(e.target.value || null)}
                  className="audio-select"
                >
                  <option value="">未关联</option>
                  {audioFiles.map(audio => (
                    <option key={audio.name} value={audio.name}>
                      {audio.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* 标签页切换 */}
              <div className="edit-tabs">
                <button 
                  className={`tab-btn ${activeEditTab === 'timestamps' ? 'active' : ''}`}
                  onClick={() => setActiveEditTab('timestamps')}
                >
                  ⏱️ 时间戳设置
                </button>
                <button 
                  className={`tab-btn ${activeEditTab === 'questions' ? 'active' : ''}`}
                  onClick={() => setActiveEditTab('questions')}
                >
                  📝 阅读理解题目
                </button>
              </div>
              
              {/* 时间戳设置面板 */}
              {activeEditTab === 'timestamps' && (
                <div className="edit-section">
                  <h3>⏱️ 段落音频时间戳设置</h3>
                  <p className="section-desc">为每个段落设置音频开始和结束时间</p>
                  {articleDetail?.audioFile && (
                    <p className="section-desc">🎵 音频文件: {articleDetail.audioFile} | 总时长: {audioDuration > 0 ? formatDuration(audioDuration) : '加载中...'}</p>
                  )}
                  
                  {articleDetail?.paragraphs && articleDetail.paragraphs.length > 0 ? (
                    <div className="timestamps-list">
                      {articleDetail.paragraphs.map((paragraph, index) => {
                        const isLast = index === articleDetail.paragraphs.length - 1;
                        const startTime = secondsToTime(paragraph.audioStart);
                        const endTime = secondsToTime(paragraph.audioEnd);
                        return (
                          <div key={index} className="timestamp-item">
                            <div className="timestamp-header">
                              <span className="timestamp-number">段落 {index + 1}</span>
                              {isLast && audioDuration > 0 && (
                                <span className="auto-fill-hint">（自动填充音频结束时间）</span>
                              )}
                              <span className="timestamp-preview">
                                {paragraph.original?.substring(0, 50)}...
                              </span>
                            </div>
                            <div className="timestamp-inputs">
                              <div className="timestamp-field-group">
                                <label>开始时间</label>
                                <div className="time-input-row">
                                  <input
                                    type="number"
                                    step="1"
                                    min="0"
                                    value={startTime.minutes || ''}
                                    onChange={(e) => updateParaTimestamp(index, 'audioStart', e.target.value, 'minutes')}
                                    placeholder="0"
                                    className="time-input-minutes"
                                  />
                                  <span className="time-separator">:</span>
                                  <input
                                    type="number"
                                    step="0.1"
                                    min="0"
                                    max="59.9"
                                    value={startTime.seconds || ''}
                                    onChange={(e) => updateParaTimestamp(index, 'audioStart', e.target.value, 'seconds')}
                                    placeholder="00"
                                    className="time-input-seconds"
                                  />
                                </div>
                              </div>
                              <div className="timestamp-separator">→</div>
                              <div className="timestamp-field-group">
                                <label>结束时间</label>
                                <div className="time-input-row">
                                  <input
                                    type="number"
                                    step="1"
                                    min="0"
                                    value={endTime.minutes || ''}
                                    onChange={(e) => updateParaTimestamp(index, 'audioEnd', e.target.value, 'endMinutes')}
                                    placeholder="0"
                                    className="time-input-minutes"
                                  />
                                  <span className="time-separator">:</span>
                                  <input
                                    type="number"
                                    step="0.1"
                                    min="0"
                                    max="59.9"
                                    value={endTime.seconds || ''}
                                    onChange={(e) => updateParaTimestamp(index, 'audioEnd', e.target.value, 'endSeconds')}
                                    placeholder="00"
                                    className="time-input-seconds"
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="empty-state">该文章暂无段落内容</p>
                  )}
                </div>
              )}
              
              {/* 阅读理解题目编辑面板 */}
              {activeEditTab === 'questions' && (
                <div className="edit-section">
                  <h3>📝 阅读理解题目</h3>
                  <p className="section-desc">为文章添加阅读理解题目（支持3-5道）</p>
                  
                  {articleDetail?.questions && articleDetail.questions.length > 0 ? (
                    <div className="questions-edit-list">
                      {articleDetail.questions.map((question, qIndex) => (
                        <div key={qIndex} className="question-edit-item">
                          <div className="question-edit-header">
                            <span className="question-number">第 {qIndex + 1} 题</span>
                            <button 
                              className="btn btn-xs btn-danger"
                              onClick={() => {
                                const newQuestions = [...articleDetail.questions];
                                newQuestions.splice(qIndex, 1);
                                setArticleDetail({ ...articleDetail, questions: newQuestions });
                              }}
                            >
                              删除
                            </button>
                          </div>
                          <input
                            type="text"
                            value={question.question}
                            onChange={(e) => {
                              const newQuestions = [...articleDetail.questions];
                              newQuestions[qIndex].question = e.target.value;
                              setArticleDetail({ ...articleDetail, questions: newQuestions });
                            }}
                            placeholder="输入题目内容（英文）"
                            className="question-input"
                          />
                          <div className="options-edit">
                            {question.options.map((option, oIndex) => (
                              <div key={oIndex} className="option-edit-item">
                                <span className="option-key-label">{option.key}</span>
                                <input
                                  type="text"
                                  value={option.text}
                                  onChange={(e) => {
                                    const newQuestions = [...articleDetail.questions];
                                    newQuestions[qIndex].options[oIndex].text = e.target.value;
                                    setArticleDetail({ ...articleDetail, questions: newQuestions });
                                  }}
                                  placeholder={`选项 ${option.key} 内容`}
                                  className="option-input"
                                />
                                <input
                                  type="radio"
                                  name={`question-${qIndex}-answer`}
                                  checked={question.correctAnswer === option.key}
                                  onChange={() => {
                                    const newQuestions = [...articleDetail.questions];
                                    newQuestions[qIndex].correctAnswer = option.key;
                                    setArticleDetail({ ...articleDetail, questions: newQuestions });
                                  }}
                                />
                              </div>
                            ))}
                          </div>
                          <textarea
                            value={question.analysis}
                            onChange={(e) => {
                              const newQuestions = [...articleDetail.questions];
                              newQuestions[qIndex].analysis = e.target.value;
                              setArticleDetail({ ...articleDetail, questions: newQuestions });
                            }}
                            placeholder="输入题目解析（中文）"
                            className="analysis-textarea"
                          />
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="empty-state">该文章暂无阅读理解题目</p>
                  )}
                  
                  <button 
                    className="btn btn-accent"
                    onClick={() => {
                      const newQuestion = {
                        question: '',
                        options: [
                          { key: 'A', text: '' },
                          { key: 'B', text: '' },
                          { key: 'C', text: '' },
                          { key: 'D', text: '' }
                        ],
                        correctAnswer: 'A',
                        analysis: ''
                      };
                      const currentQuestions = articleDetail?.questions || [];
                      setArticleDetail({ ...articleDetail, questions: [...currentQuestions, newQuestion] });
                    }}
                  >
                    ➕ 添加题目
                  </button>
                </div>
              )}
              
              {/* 模态框底部按钮 */}
              <div className="modal-footer">
                <button className="btn btn-outline" onClick={closeEditModal}>取消</button>
                <button className="btn btn-primary" onClick={saveParagraphTimestamps}>保存修改</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </AdminProtected>
  );
}